import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useBoq,
  useBoqMove,
  useDeleteBoq,
  useIssueInvoice,
  useRemoveBoqLine,
  useSetBoqLine,
  useUpdateBoqHeader,
  type BoqDetail,
} from "../../api/boq";
import { formatDate, formatDateTime } from "../../api/dates";
import { formatMoney, formatQuantity } from "../../api/money";
import { fullPriceQuoteUrl } from "../../api/quotations";
import { StatusChip } from "../../components/ui";
import ConfirmRemove from "../commercial/ConfirmRemove";
import { jobLink } from "../commercial/pipeline";
import { boqLabel, boqTone } from "../commercial/tones";
import BoqEstimatePanel from "./BoqEstimatePanel";
import K1Panel from "./K1Panel";
import LabourPanel from "./LabourPanel";
import RatePicker from "./RatePicker";
import RepricePanel from "./RepricePanel";

interface Props {
  id: number;
}

/** One bill: the works details the official workbook prints, the lines at
 * the tendered rates, the Schedule of Rates picker while it is a draft, the
 * downloads once approved, and the moves this account may make. */
export default function BoqWorkspace({ id }: Props) {
  const boq = useBoq(id);
  if (boq.isPending) {
    return <p className="empty-state">Loading the bill…</p>;
  }
  if (boq.isError) {
    return <p className="notice bad">{boq.error.message}</p>;
  }
  return <BoqView detail={boq.data} />;
}

export function BoqView({ detail }: { detail: BoqDetail }) {
  const navigate = useNavigate();
  const move = useBoqMove(detail.id);
  const remove = useDeleteBoq();
  const invoice = useIssueInvoice(detail.id);
  const a = detail.actions;
  const job = detail.quotation?.id ?? null;
  const where = [detail.contract?.name, detail.region_name].filter(Boolean).join(" · ");

  return (
    <section className="cm-docview" aria-label={`BOQ ${detail.name}`}>
      <div className="cm-doc-head">
        <h2>{detail.name}</h2>
        <StatusChip tone={boqTone(detail.status)}>{boqLabel(detail.status)}</StatusChip>
        {detail.provisional ? <StatusChip tone="warn">Provisional</StatusChip> : null}
        {detail.is_draft ? <StatusChip>Draft BOQ</StatusChip> : null}
        {where ? <span className="cm-doc-meta">{where}</span> : null}
      </div>
      {detail.provisional_reason ? <p className="notice warn">{detail.provisional_reason}</p> : null}
      {detail.is_draft ? (
        <p className="notice">
          The draft BOQ: the job priced by hand before the supplier quotes. It is never issued to the City; the customer
          BOQ is raised from it as a copy.
          {detail.copied_to ? (
            <>
              {" "}
              <Link to={`/boq/${detail.copied_to.id}`}>{detail.copied_to.name}</Link> was copied from it, so it keeps what
              was first priced. Make changes on the customer BOQ.
            </>
          ) : null}
        </p>
      ) : null}
      {detail.copied_from ? (
        <p className="notice">
          Copied from the draft BOQ <Link to={`/boq/${detail.copied_from.id}`}>{detail.copied_from.name}</Link>. Add any
          additional work here.
        </p>
      ) : null}

      <dl className="cm-facts">
        {detail.quotation ? (
          <div>
            <dt>From quotation</dt>
            <dd>
              <Link to={jobLink(detail.quotation.id, "quote")}>{detail.quotation.number}</Link>
            </dd>
          </div>
        ) : null}
        {detail.inspection || detail.work_order_no ? (
          <div>
            <dt>Works order</dt>
            <dd>{detail.inspection?.work_order_no ?? detail.work_order_no}</dd>
          </div>
        ) : null}
        <div>
          <dt>Dated</dt>
          <dd>{formatDate(detail.boq_date)}</dd>
        </div>
        {detail.approved_at ? (
          <div>
            <dt>Approved</dt>
            <dd>
              {formatDateTime(detail.approved_at)} by {detail.approved_by ?? "—"}
            </dd>
          </div>
        ) : null}
        {detail.issued_at ? (
          <div>
            <dt>Issued to the City</dt>
            <dd>
              {formatDateTime(detail.issued_at)} by {detail.issued_by ?? "—"}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Invoice</dt>
          <dd>
            {detail.invoice ? (
              <Link to={job !== null ? jobLink(job, "invoice") : `/invoices/${detail.invoice.id}`}>{detail.invoice.number}</Link>
            ) : a.can_invoice ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={invoice.isPending}
                onClick={() =>
                  invoice.mutate(undefined, {
                    onSuccess: (made) => navigate(job !== null ? jobLink(job, "invoice") : `/invoices/${made.invoice_id}`),
                  })
                }
              >
                Issue the tax invoice
              </button>
            ) : (
              <span className="muted">{detail.invoice_blocked ?? "—"}</span>
            )}
            {invoice.isError ? <span className="line-warn"> {invoice.error.message}</span> : null}
          </dd>
        </div>
      </dl>
      {detail.estimate ? <BoqEstimatePanel estimate={detail.estimate} /> : null}

      <Header detail={detail} />
      <Lines detail={detail} />
      <K1Panel detail={detail} />

      {a.can_edit ? <RatePicker detail={detail} /> : null}

      <Documents detail={detail} />

      <RepricePanel detail={detail} />
      <LabourPanel detail={detail} />
      <section className="cm-block">
        <h3>Where it stands: {boqLabel(detail.status)}</h3>
        <div className="move-buttons">
          {a.can_issue ? (
            <button type="button" className="btn btn-primary" disabled={move.isPending} onClick={() => move.mutate("issue")}>
              Issue to the City
            </button>
          ) : null}
          {a.can_withdraw ? (
            <button type="button" className="btn btn-secondary" disabled={move.isPending} onClick={() => move.mutate("withdraw")}>
              Withdraw from the City
            </button>
          ) : null}
          {a.can_unapprove ? (
            <button type="button" className="btn btn-secondary" disabled={move.isPending} onClick={() => move.mutate("unapprove")}>
              Unapprove to correct it
            </button>
          ) : null}
          {a.can_delete ? (
            <button
              type="button"
              className="btn btn-danger"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete ${detail.name}? This cannot be undone.`)) {
                  // Stay on the job: its Customer BOQ step goes back to "not
                  // raised". Going to /boq lost the job altogether (Fix #6).
                  remove.mutate(detail.id, {
                    onSuccess: () => navigate(job !== null ? jobLink(job, "boq") : "/commercial", { replace: true }),
                  });
                }
              }}
            >
              Delete draft
            </button>
          ) : null}
        </div>
        {!a.can_issue && a.can_edit && detail.issue_blocked ? <p className="notice warn">{detail.issue_blocked}</p> : null}
        {move.isError ? <p className="notice bad">{move.error.message}</p> : null}
        {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
      </section>

      <section className="cm-block">
        <h3>Revisions</h3>
        {detail.revisions.length === 0 ? (
          <p className="cm-doc-note">No changes recorded yet.</p>
        ) : (
          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>When</th>
                  <th>By</th>
                  <th>What</th>
                  <th>Status</th>
                  <th className="num">Excl VAT</th>
                </tr>
              </thead>
              <tbody>
                {detail.revisions.map((row) => (
                  <tr key={row.revision_no}>
                    <td>{row.revision_no}</td>
                    <td>{formatDateTime(row.at)}</td>
                    <td>{row.by ?? "—"}</td>
                    <td>{row.reason}</td>
                    <td>{boqLabel(row.status)}</td>
                    <td className="num">{formatMoney(row.total_excl_vat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {detail.overrides.length > 0 ? (
          <>
            <h4>Figures changed from what was calculated</h4>
            <ul className="cm-overrides">
              {detail.overrides.map((o, index) => (
                <li key={`${o.payment_ref}-${o.at}-${index}`}>
                  {o.payment_ref ?? "A line"} {o.field}: {formatMoney(o.calculated)} → {formatMoney(o.revised)} —{" "}
                  {o.reason ?? "no reason"} ({o.by ?? "—"}, {o.role ?? "—"}, {formatDate(o.at)})
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
    </section>
  );
}

/** The bill's workbook and the full-price quote that goes to the City with it
 * -- the supplier's quotation with their standing discount left off, which
 * lived on the Supplier quote step until the office asked for it here. A bill
 * with no quotation behind it offers its own PDF instead. */
function Documents({ detail }: { detail: BoqDetail }) {
  const blocked = detail.export_blocked;
  const quote = detail.quotation;
  return (
    <section className="cm-block">
      <h3>Documents</h3>
      {blocked ? <p className="notice warn">{blocked}</p> : null}
      <div className="move-buttons">
        {blocked ? null : (
          <a className="btn btn-primary" href={detail.urls.xlsm}>
            {detail.draft_copy ? "Draft BOQ workbook (.xlsm)" : "Official BOQ workbook (.xlsm)"}
          </a>
        )}
        {quote !== null ? (
          <a className="btn btn-secondary" href={fullPriceQuoteUrl(quote.id)}>
            Full price quote (PDF)
          </a>
        ) : blocked ? null : (
          <a className="btn btn-secondary" href={detail.urls.pdf}>
            PDF
          </a>
        )}
        {!blocked && detail.has_remittance ? (
          <>
            <a className="btn btn-secondary" href={detail.urls.remittance}>
              Remittance
            </a>
            <a className="btn btn-secondary" href={detail.urls.customer_summary}>
              Customer summary
            </a>
          </>
        ) : null}
      </div>
      <p className="cm-doc-note">
        Only the billed rows and their headings show in the workbook; every rate and total stays the tender's own
        formula.
      </p>
    </section>
  );
}

function Header({ detail }: { detail: BoqDetail }) {
  const update = useUpdateBoqHeader(detail.id);
  const [name, setName] = useState(detail.name);
  const [projectNo, setProjectNo] = useState(detail.project_no ?? "");
  const [projectDesc, setProjectDesc] = useState(detail.project_desc ?? "");
  const [scope, setScope] = useState(detail.scope ?? "");
  const [siteLabel, setSiteLabel] = useState(detail.site_label ?? "");
  const [boqDate, setBoqDate] = useState(detail.boq_date ?? "");
  const editable = detail.actions.can_edit;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    update.mutate({
      name: name.trim(),
      project_no: projectNo.trim() || null,
      project_desc: projectDesc.trim() || null,
      scope: scope.trim() || null,
      site_label: siteLabel.trim() || null,
      boq_date: boqDate || null,
    });
  }

  return (
    <form className="header-form cm-form" onSubmit={onSubmit}>
      <legend>Works details, as the official workbook prints them</legend>
      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} disabled={!editable} required />
      </label>
      <label>
        Works Project No.
        <input value={projectNo} onChange={(e) => setProjectNo(e.target.value)} disabled={!editable} placeholder="502-XXX-1" />
      </label>
      <label>
        Project description
        <input value={projectDesc} onChange={(e) => setProjectDesc(e.target.value)} disabled={!editable} />
      </label>
      <label className="wide">
        Scope
        <input value={scope} onChange={(e) => setScope(e.target.value)} disabled={!editable} />
      </label>
      <label>
        Site (as printed)
        <input value={siteLabel} onChange={(e) => setSiteLabel(e.target.value)} disabled={!editable} placeholder={detail.site?.name ?? ""} />
      </label>
      <label>
        Date
        <input type="date" value={boqDate} onChange={(e) => setBoqDate(e.target.value)} disabled={!editable} />
      </label>
      {editable ? (
        <button type="submit" className="btn btn-secondary" disabled={update.isPending}>
          Save works details
        </button>
      ) : null}
      {update.isError ? <p className="notice bad">{update.error.message}</p> : null}
    </form>
  );
}

function Lines({ detail }: { detail: BoqDetail }) {
  const setLine = useSetBoqLine(detail.id);
  const removeLine = useRemoveBoqLine(detail.id);
  const [editing, setEditing] = useState<number | null>(null);
  const [qty, setQty] = useState("");
  const editable = detail.actions.can_edit;
  const error = setLine.error ?? removeLine.error;

  return (
    <div className="table-card">
      <table className="data-table cm-lines" aria-label="Bill lines">
        <thead>
          <tr>
            <th>Payment ref</th>
            <th>Description</th>
            <th>Unit</th>
            <th className="num">Qty</th>
            <th className="num">Rate</th>
            <th className="num">Total ex VAT</th>
            {editable ? <th aria-label="Line tools" /> : null}
          </tr>
        </thead>
        <tbody>
          {detail.lines.length === 0 ? (
            <tr>
              <td colSpan={editable ? 7 : 6} className="muted">
                No lines yet. Pick them off the Schedule of Rates below.
              </td>
            </tr>
          ) : null}
          {detail.lines.map((line) => (
            <tr key={line.item_id} className={line.rate === null && line.rate_note === null ? "cm-line-warn" : undefined}>
              <td className="mono">
                {line.payment_ref}
                <span className="sub">Item {line.item_no}</span>
              </td>
              <td>{line.description}</td>
              <td className="muted">{line.unit ?? ""}</td>
              <td className="num">
                {editing === line.rate_item_id ? (
                  <input value={qty} onChange={(e) => setQty(e.target.value)} className="input num" aria-label={`${line.payment_ref} quantity`} autoFocus />
                ) : (
                  formatQuantity(line.qty)
                )}
              </td>
              <td className="num">{line.rate_note ?? (line.rate === null ? <em>unpriced</em> : formatMoney(line.rate))}</td>
              <td className="num">{formatMoney(line.line_total)}</td>
              {editable ? (
                <td className="cm-tools">
                  {editing === line.rate_item_id ? (
                    <>
                      <button
                        type="button"
                        className="row-button cm-save"
                        disabled={setLine.isPending}
                        onClick={() => setLine.mutate({ rate_item_id: line.rate_item_id, qty }, { onSuccess: () => setEditing(null) })}
                      >
                        Save
                      </button>
                      <button type="button" className="row-button" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="row-button"
                        aria-label={`Edit ${line.payment_ref} quantity`}
                        onClick={() => {
                          setEditing(line.rate_item_id);
                          setQty(line.qty);
                        }}
                      >
                        Qty
                      </button>
                      <ConfirmRemove what={line.payment_ref} disabled={removeLine.isPending} onConfirm={() => removeLine.mutate(line.item_id)} />
                    </>
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}>Excluding VAT</td>
            <td className="num">{formatMoney(detail.total_excl_vat)}</td>
            {editable ? <td /> : null}
          </tr>
        </tfoot>
      </table>
      {detail.unpriced.length > 0 ? (
        <p className="notice warn">Unpriced on the rate card: {detail.unpriced.join(", ")}. The total leaves them out.</p>
      ) : null}
      {error ? <p className="notice bad">{error.message}</p> : null}
    </div>
  );
}
